import type { CSSProperties, Ref } from "vue";
import { computed, ref, watchEffect } from "vue";

import type { BoardMeta } from "@/components/board/injectKeys";
import { boardFromRelative, boardScaleFromRelative } from "@/math/coordinate-transform";
import { validateCardPositionInBoard } from "@/math/coordinate-utils";
import { boardAspect, cardZoomHeight, stickySize } from "@/model/Settings";
import type { Board } from "@/model/board";
import type { Card, CardMeta } from "@/model/card";
import { initBoardCoord, relativeCoord } from "@/model/coordinates";
import { isFullyFaded } from "@/model/markMode";
import variables from "@/model/variable.module.scss";
import { useBoardStore } from "@/store/board";
import { cardColor } from "@/store/card";
import { useDraggingStore } from "@/store/dragging";
import { useZoomStore } from "@/store/zoom";
import { contrastCssColor, toCssColor } from "@/utils/color";

type Args = Readonly<{
  card: Ref<Card>;
  cardMeta: Ref<CardMeta>;
  board: Ref<Board>;
  isEnlarged: Ref<Readonly<boolean>>;
  isActive: Ref<Readonly<boolean>>;
  boardMeta: Ref<BoardMeta>;
}>;

export const useStyles = ({ card, board, cardMeta, isEnlarged, isActive, boardMeta }: Args) => {
  // Track whether this card's board is the currently active board
  const isCurrentBoard = computed(() => useBoardStore().currentBoard().id === board.value.id);

  // Using ref + watchEffect instead of computed for expensive reactive chains
  // When board is inactive, watchEffect returns early without tracking expensive dependencies
  // This avoids unnecessary reactivity overhead for off-screen cards

  const sizes = ref({ relative: relativeCoord(0, 0), board: initBoardCoord(), scale: 1 });

  watchEffect(() => {
    if (!isCurrentBoard.value) return; // Board inactive - return early
    const { relative, scale } = calcSize();
    sizes.value = {
      relative,
      board: boardScaleFromRelative(relative, { board: boardMeta.value.size }),
      scale,
    };
  });

  function calcSize() {
    const normal = board.value.cardSize;
    if (isEnlarged.value) {
      const enlargedY = cardZoomHeight / useZoomStore().factor;
      const scale = enlargedY / normal.y;
      if (scale > 1) {
        return { relative: relativeCoord(enlargedY / boardAspect, enlargedY), scale };
      }
    }
    return { relative: normal, scale: 1 };
  }

  const zIndex = computed(() => {
    if (isEnlarged.value) {
      return board.value.maxZ + 100;
    }

    return isFullyFaded(cardMeta.value.mark) && !isActive.value
      ? +variables.zIndexLinks - 1
      : +variables.zIndexLinks + 1 + cardMeta.value.zIndex;
  });

  const position = computed(() => {
    const dragPos = useDraggingStore().dragging[card.value.id]?.pos;
    const relativePos = dragPos || cardMeta.value.pos;
    const boardPos = boardFromRelative(relativePos, {
      board: boardMeta.value.size,
      card: sizes.value.relative,
    });
    const offset = validateCardPositionInBoard(boardPos, sizes.value.board, isEnlarged.value);
    return { ...boardPos, offset };
  });

  const backgroundColor = computed(() => cardColor(card.value, board.value));

  const contrastingTextColor = computed(() => contrastCssColor(backgroundColor.value));

  const styles = computed<CSSProperties>(() => ({
    zIndex: zIndex.value,
    color: contrastingTextColor.value,
    backgroundColor: toCssColor(backgroundColor.value),
    top: position.value.y + "px",
    left: position.value.x + "px",
    transform: `scale(${sizes.value.board.x / stickySize})`,
  }));

  return { styles, sizes, position };
};
